import { Link } from "react-router-dom";
import { Instagram, Linkedin, Facebook, Mail, CalendarDays } from "lucide-react";
import { NAV_LINKS, BRAND, SOCIAL_LABELS } from "@/lib/content";
import { useSettings } from "@/lib/SettingsContext";
import NewsletterSignupForm from "@/components/site/NewsletterSignupForm";

const SOCIAL_ICONS = {
  instagram_url: Instagram,
  linkedin_url: Linkedin,
  facebook_url: Facebook,
};

export default function SiteFooter() {
  const { settings } = useSettings();
  const year = new Date().getFullYear();
  const email = settings.public_contact_email || BRAND.contactEmail;
  const calendly = settings.calendly_url;

  const socials = Object.entries(SOCIAL_LABELS).filter(([k]) => settings[k]);

  return (
    <footer
      data-testid="site-footer"
      className="mt-20"
      style={{ backgroundColor: "var(--wt-ground)", color: "var(--wt-bg)" }}
    >
      <div className="wt-container py-14">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10">
          <div className="md:col-span-5">
            <p className="font-serif text-2xl leading-tight">{BRAND.name}</p>
            <p
              className="mt-2 text-[12px] uppercase tracking-[0.16em]"
              style={{ color: "rgba(250,247,240,0.7)" }}
            >
              {BRAND.title}
            </p>
            <div className="mt-4 h-px w-12" style={{ backgroundColor: "var(--wt-gold)" }} />
            <p
              className="mt-5 text-sm leading-relaxed max-w-md"
              style={{ color: "rgba(250,247,240,0.82)" }}
            >
              School climate is not soft work. It is protective infrastructure. Wanakee partners
              with districts, schools, and community leaders to build durable systems their teams
              can implement, monitor, and sustain.
            </p>

            <div className="mt-6 flex flex-col gap-2 text-sm">
              <a
                href={`mailto:${email}`}
                data-testid="footer-email-link"
                className="inline-flex items-center gap-2 hover:underline underline-offset-4"
                style={{ color: "rgba(250,247,240,0.92)" }}
              >
                <Mail size={14} aria-hidden="true" /> {email}
              </a>
              {calendly ? (
                <a
                  href={calendly}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="footer-calendly-link"
                  className="inline-flex items-center gap-2 hover:underline underline-offset-4"
                  style={{ color: "rgba(250,247,240,0.92)" }}
                >
                  <CalendarDays size={14} aria-hidden="true" /> Open scheduling
                </a>
              ) : (
                <Link
                  to="/book-consultation"
                  data-testid="footer-consultation-link"
                  className="inline-flex items-center gap-2 hover:underline underline-offset-4"
                  style={{ color: "rgba(250,247,240,0.92)" }}
                >
                  <CalendarDays size={14} aria-hidden="true" /> Book a Consultation
                </Link>
              )}
            </div>

            {socials.length > 0 && (
              <div className="mt-6 flex items-center gap-3">
                {socials.map(([key, label]) => {
                  const Icon = SOCIAL_ICONS[key];
                  return (
                    <a
                      key={key}
                      href={settings[key]}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label={label}
                      data-testid={`footer-social-${label.toLowerCase()}`}
                      className="inline-flex h-9 w-9 items-center justify-center rounded-md"
                      style={{
                        border: "1px solid rgba(250,247,240,0.2)",
                        color: "var(--wt-bg)",
                      }}
                    >
                      <Icon size={15} />
                    </a>
                  );
                })}
              </div>
            )}
          </div>

          <div className="md:col-span-3">
            <p
              className="text-[11px] font-semibold tracking-[0.16em] uppercase"
              style={{ color: "rgba(250,247,240,0.6)" }}
            >
              Navigate
            </p>
            <ul className="mt-3 flex flex-col gap-2">
              {NAV_LINKS.map((link) => (
                <li key={link.to}>
                  <Link
                    to={link.to}
                    data-testid={`footer-link-${link.label.toLowerCase()}`}
                    className="text-sm hover:underline underline-offset-4"
                    style={{ color: "rgba(250,247,240,0.88)" }}
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
              <li>
                <Link
                  to="/book-consultation"
                  data-testid="footer-link-consultation"
                  className="text-sm hover:underline underline-offset-4"
                  style={{ color: "rgba(250,247,240,0.88)" }}
                >
                  Book a Consultation
                </Link>
              </li>
              <li>
                <Link
                  to="/book-speaking"
                  data-testid="footer-link-speaking"
                  className="text-sm hover:underline underline-offset-4"
                  style={{ color: "rgba(250,247,240,0.88)" }}
                >
                  Book Me to Speak
                </Link>
              </li>
              <li>
                <Link
                  to="/privacy"
                  data-testid="footer-link-privacy"
                  className="text-sm hover:underline underline-offset-4"
                  style={{ color: "rgba(250,247,240,0.88)" }}
                >
                  Privacy Policy
                </Link>
              </li>
            </ul>
          </div>

          <div className="md:col-span-4">
            <p
              className="text-[11px] font-semibold tracking-[0.16em] uppercase"
              style={{ color: "rgba(250,247,240,0.6)" }}
            >
              Stay close to the work
            </p>
            <p className="mt-3 text-sm" style={{ color: "rgba(250,247,240,0.82)" }}>
              Occasional notes on school climate, partnership, and the systems that hold student
              success together.
            </p>
            <NewsletterSignupForm source="footer" variant="dark" />
          </div>
        </div>

        <div
          className="mt-12 pt-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3"
          style={{ borderTop: "1px solid rgba(232,226,216,0.18)" }}
        >
          <p className="text-xs" style={{ color: "rgba(250,247,240,0.6)" }}>
            &copy; {year} {BRAND.name}. All rights reserved.
          </p>
          <p
            className="text-[11px] tracking-[0.14em] uppercase"
            style={{ color: "rgba(250,247,240,0.5)" }}
          >
            wanakeetrask.com
          </p>
        </div>
      </div>
    </footer>
  );
}
